import type { PermisoCatalogo, PermisoModulo, PermisoPagina } from '../types/permisos';
import { findModuloInCatalogo, resolvePagePermissionKey } from '../config/permisosCatalogoHelper';
import {
  extractRouteParams,
  isSystemAccessRule,
  resolveRouteAccess,
} from '../config/routeAccess';
import { ALWAYS_ALLOWED_PATHS, pathToPermissionKey } from './permisoKeys';
import { canManageSistemaUsers } from './sistemaAuthz';

function toSet(granted: ReadonlySet<string> | string[]): Set<string> {
  return granted instanceof Set ? granted : new Set(granted);
}

export interface PageAccessContext {
  roles: string[];
  username: string;
}

export function bypassesBusinessPermissions(roles: string[] | undefined): boolean {
  return canManageSistemaUsers(roles);
}

function hasKnownKeys(knownKeys?: ReadonlySet<string>): knownKeys is ReadonlySet<string> {
  return Boolean(knownKeys && knownKeys.size > 0);
}

/** Módulo completo cubre páginas y controles; la página no cubre controles. */
export function canPermission(
  granted: ReadonlySet<string> | string[],
  key: string,
  bypass: boolean,
  knownKeys?: ReadonlySet<string>,
): boolean {
  if (!key) return false;
  if (hasKnownKeys(knownKeys) && !knownKeys.has(key)) return false;
  if (bypass) return true;
  const set = toSet(granted);
  if (set.has(key)) return true;
  const moduleKey = key.split('.')[0];
  return Boolean(moduleKey && set.has(moduleKey));
}

export function isPageGranted(granted: ReadonlySet<string> | string[], pageKey: string): boolean {
  if (!pageKey) return false;
  const set = toSet(granted);
  if (set.has(pageKey)) return true;
  const moduleKey = pageKey.split('.')[0];
  if (moduleKey && set.has(moduleKey)) return true;
  const prefix = `${pageKey}.`;
  return Array.from(set).some((item) => item.startsWith(prefix));
}

export function canPagePermission(
  granted: ReadonlySet<string> | string[],
  pathname: string,
  bypass: boolean,
  catalogo: PermisoCatalogo,
  access?: PageAccessContext,
): boolean {
  const path = (pathname.split('?')[0] || '/').replace(/\/+$/, '') || '/';
  if (ALWAYS_ALLOWED_PATHS.has(path)) return true;

  if (access) {
    const resolved = resolveRouteAccess({
      pathname: path,
      params: extractRouteParams(path),
      roles: access.roles,
      username: access.username,
    });
    if (resolved.decision === 'deny') return false;
    if (isSystemAccessRule(resolved.ruleId)) return true;
  }

  // Sin catálogo aún: usar clave por path + granted en caché (evita grilla vacía → pop).
  let key = resolvePagePermissionKey(path, catalogo);
  if (!key && catalogo.modulos.length === 0) {
    key = pathToPermissionKey(path) || null;
  }
  if (bypass) return Boolean(key) || catalogo.modulos.length === 0;
  if (!key) return false;
  return isPageGranted(granted, key);
}

export function canModulePermission(
  granted: ReadonlySet<string> | string[],
  pathOrKey: string,
  bypass: boolean,
  catalogo: PermisoCatalogo,
): boolean {
  const modulo = findModuloInCatalogo(catalogo, pathOrKey);
  if (bypass) return Boolean(modulo) || catalogo.modulos.length === 0;
  if (!modulo) return false;
  if (isPageGranted(granted, modulo.key)) return true;
  return modulo.paginas.some((page: PermisoPagina) => isPageGranted(granted, page.key));
}

export function compactGrantedKeys(expanded: Iterable<string>, catalogo: PermisoCatalogo): string[] {
  const set = new Set(expanded);
  const result: string[] = [];

  catalogo.modulos.forEach((mod) => {
    const desc = moduleDescendantKeys(mod);
    if (desc.length > 0 && desc.every((k) => set.has(k))) {
      result.push(mod.key);
      return;
    }
    if (desc.length === 0 && set.has(mod.key)) {
      result.push(mod.key);
      return;
    }
    mod.paginas.forEach((page) => {
      if (set.has(page.key)) result.push(page.key);
      page.controles.forEach((control) => {
        if (set.has(control.key)) result.push(control.key);
      });
    });
  });

  return Array.from(new Set(result));
}

export function expandGrantedKeys(
  granted: Iterable<string>,
  allKeys: string[],
  catalogo: PermisoCatalogo,
): Set<string> {
  const source = granted instanceof Set ? granted : new Set(granted);
  const expanded = new Set<string>();
  catalogo.modulos.forEach((mod) => {
    if (source.has(mod.key)) {
      expanded.add(mod.key);
      moduleDescendantKeys(mod).forEach((k) => expanded.add(k));
    }
  });
  allKeys.forEach((key) => {
    if (source.has(key)) expanded.add(key);
  });
  return expanded;
}

function moduleDescendantKeys(mod: PermisoModulo): string[] {
  const keys: string[] = [];
  mod.paginas.forEach((page) => {
    keys.push(page.key);
    page.controles.forEach((c) => keys.push(c.key));
  });
  return keys;
}
